import { Component } from '@angular/core';
import { RouteReuseStrategy, DetachedRouteHandle, ActivatedRouteSnapshot, Data } from '@angular/router';
import { v4 as uuidv4 } from 'uuid';

/**
 * Implement this interface when the component is heavy and should be cached, e.g. a large data
 * grid AND the component needs to take an action each time it is restored from the cache and
 * reattached to the UI.
 *
 * The component route will also need to be annotated with correct data so that it is cached.
 * See {@link createReusableComponentRouteData}.
 *
 * Child components may need to handle listening for route context changes.
 * See {@link CustomRouteReuseStrategy}.
 */
export interface ReusableComponent extends Component {
	/**
	 * Called each time the cached component instance is restored from cache and reattached.
	 */
	onComponentRestore(): void;
}

interface ReusableRouteHandle extends DetachedRouteHandle {
	componentRef: {
		instance: ReusableComponent;
	};
}

export enum ComponentCacheKeyStrategy {
	/**
	 * A unique fixed value will be generated (guid) and used as the cache key.
	 *
	 * Child components where the route has dynamic ids, e.g. the Program > Project (with id) >
	 * Project tasks component, will need to listen for and handle changes to the route context,
	 * because the exact same component instance may be used to handle different routes where
	 * only the project id (or query parameters) change.
	 */
	FixedValue = 'FixedValue',
}

export interface ReusableRouteParams extends Data {
	cacheStrategy?: ComponentCacheKeyStrategy;
}

export type ReusableComponentRouteData = Data & {
	reuse: true;
} & { componentCacheKeyStrategy: ComponentCacheKeyStrategy.FixedValue; cacheKey: string };

/**
 * Custom implementation of Angular's RouteReuseStrategy to enable selective route caching.
 *
 * To cache a route, use {@link createReusableComponentRouteData} to create its route data.
 *
 * Use this when you want to preserve the component's state and avoid re-creating it
 * each time the user navigates away and returns (e.g., for forms, dashboards, or lists).
 */
export class CustomRouteReuseStrategy implements RouteReuseStrategy {
	/**
	 * A map to store detached route handles. The cache key is deterimined by the route configuration data.
	 * See {@link ComponentCacheKeyStrategy}.
	 */
	private storedHandles = new Map<string, DetachedRouteHandle>();

	/**
	 *  Determines whether the route should be detached (cached).
	 */
	shouldDetach(route: ActivatedRouteSnapshot): boolean {
		return this.isReusableComponentRouteData(route.data);
	}

	/**
	 * Stores the detached route handle in the cache.
	 */
	store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle): void {
		if (!this.isReusableComponentRouteData(route.data)) {
			return;
		}

		const cacheKey = this.getCacheKey(route.data);

		if (cacheKey) {
			this.storedHandles.set(cacheKey, handle);
		}
	}

	/**
	 * Determines whether the route should be reattached (restored from cache)
	 */
	shouldAttach(route: ActivatedRouteSnapshot): boolean {
		if (!this.isReusableComponentRouteData(route.data)) {
			return false;
		}

		const cacheKey = this.getCacheKey(route.data);

		return this.storedHandles.has(cacheKey);
	}

	retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
		if (!this.isReusableComponentRouteData(route.data)) {
			return false;
		}

		const cacheKey = this.getCacheKey(route.data);
		const routeHandle = this.storedHandles.get(cacheKey) ?? null;

		if (this.isReusableRouteHandle(routeHandle)) {
			routeHandle.componentRef.instance.onComponentRestore();
		}

		return routeHandle;
	}

	private getCacheKey(data: ReusableComponentRouteData): string {
		return data.cacheKey;
	}

	private isReusableRouteHandle(routeHandle: DetachedRouteHandle): routeHandle is ReusableRouteHandle {
		return (
			routeHandle &&
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			(routeHandle as any)?.componentRef?.instance?.onComponentRestore &&
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			typeof (routeHandle as any)?.componentRef?.instance?.onComponentRestore === 'function'
		);
	}

	private isReusableComponentRouteData(data: ActivatedRouteSnapshot['data']): data is ReusableComponentRouteData {
		return data && 'reuse' in data && data.reuse === true;
	}

	shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
		return future.routeConfig === curr.routeConfig;
	}
}

/**
 * Creates route data to enable caching of a component. It a component will be reused when a given
 * route is re-accessed. Note it will be the exact same component with the same internal state.
 *
 * It can be used as a performance improvement when components are heavy, with lots of data.
 *
 * It can also be used as a UX improvement, because the user can resume what they were doing,
 * rather than needing to filter and load the data again.
 *
 * Note this uses the default, and currently only implementd cache key strategy of {@link ComponentCacheKeyStrategy.FixedValue}.
 * This means the component instance will need to handle changes to the route context and update its
 * data accordingly.
 *
 * E.g. the Program > Project (with id) > Project tasks component would need to handle when the project id
 * in the route changes if it was enabled for reuse.
 */
export const createReusableComponentRouteData = (params: ReusableRouteParams = {}): ReusableComponentRouteData => {
	return {
		...params,
		reuse: true,
		componentCacheKeyStrategy: params.cacheStrategy ?? (ComponentCacheKeyStrategy.FixedValue as const),
		cacheKey: uuidv4(),
	};
};
